/**
  * @class msysLogger
  * @description
  * Provides messaging interface to logger service.
  * Also configures logger service on startup.
  */

import msysListener from 'msysListener'
const perf_hooks = require('perf_hooks')
const monitorEventLoopDelay = perf_hooks?.monitorEventLoopDelay
const PerformanceObserver = perf_hooks?.PerformanceObserver

export default class msysLogger extends msysListener {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([], dependencies)
    cfg_schema = _.extend({
      level: {
        type: 'string',
        description: 'logging level (none, all)',
        enum: ['none', '', 'default', 'all'],
      },
      filter: {
        type: 'array',
        description: 'filtering rules for logging; if not provided, will use filter based on type of application: log_server/log_gui/log_box.json from msysMessage/filters directory',
      },
      max_length: {
        type: 'integer',
        default: 400,
      },
      debug: {
        type: 'boolean',
        default: true,
        description: 'enable logging of debug',
      },
      log: {
        type: 'boolean',
        default: true,
        description: 'enable logging of log',
      },
      info: {
        type: 'boolean',
        default: true,
        description: 'enable logging of info',
      },
      warn: {
        type: 'boolean',
        default: true,
        description: 'enable logging of warn',
      },
      error: {
        type: 'boolean',
        default: true,
        description: 'enable logging of error',
      },
      msg: {
        type: 'boolean',
        default: true,
        description: 'enable logging of message',
      },
      event_loop_check: {
        type: 'boolean',
        default: true,
        description: 'enable event loop check',
      },
      event_loop_error_ms: {
        type: 'integer',
        default: 100,
        description: 'threshold in ms for reporting event loop lag as error',
      },
      event_loop_warn_ms: {
        type: 'integer',
        default: 40,
        description: 'threshold in ms for reporting event loop lag as warning',
      },
      gc_check: {
        type: 'boolean',
        default: true,
        description: 'enable garbage collection check',
      },
      gc_error_ms: {
        type: 'integer',
        default: 100,
        description: 'threshold in ms for reporting garbage collection pause as error',
      },
      gc_warn_ms: {
        type: 'integer',
        default: 40,
        description: 'threshold in ms for reporting garbage collection pause as warning',
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)

    const { max_length } = this.config
    let filter
    if(this.config.level === 'none') {
      msysListener.prototype.debug = function() {}
      console.logger_worker.debug = function() {}
      console.logger_worker.log = function() {}
      console.logger_worker.info = function() {}
      console.logger_worker.warn = function() {}
      console.logger_worker.error = function() {}
      console.logger_worker.message = function() {}
    }else if(this.config.level === 'all') {
      // log everything
      filter = [['APPROVE']]
      console.logger_worker.postMessage({ config: { filter, max_length } })
    }else {
      filter = this.config.filter
      if(!Array.isArray(filter)) {
        filter = [['INCLUDE', 'log_'+(MOSY_SERVER?'server':MOSY_GUI?'gui':'box')]]
      }
    }

    if(this.config.level !== 'all') {
      // disable debugging of selected things
      if(!this.config.debug) {
        msysListener.prototype.debug = function() {}
        console.logger_worker.debug = function() {}
      }
      if(!this.config.log) {
        console.logger_worker.log = function() {}
      }
      if(!this.config.info) {
        msysListener.prototype.info = function() {}
        console.logger_worker.info = function() {}
      }
      if(!this.config.warn) {
        msysListener.prototype.warn = function() {}
        console.logger_worker.warn = function() {}
      }
      if(!this.config.error) {
        msysListener.prototype.error = function() {}
        console.logger_worker.error = function() {}
      }
      if(!this.config.msg) {
        console.logger_worker.message = function() {}
      }
    }

    // configure the logger worker
    if(filter) {
      console.logger_worker.postMessage({ config: { filter, max_length } })
    }
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Response to mmExit that the listener has exited. Also fired on unsuccessful start of the listener. Sent to #ALL listeners.
  * @param {object} sender - message sender
  * @param {object} params - not used
  */
  mmExited(sender, params) {
    if(this.exitTimeout) return
    const listeners = this.getListeners()
    const running = Object.keys(listeners).filter((id)=>{ return listeners[id]!=='exited' })
    if(running.length===1 && running[0]===this.listener_id) {
      // only we are still running, so log that application has ended and exit
      this.exitTimeout = setTimeout(()=>{
        console.logger_worker.info('system', `ENDED APPLICATION ${MOSY_APP_WITH_VERSION}`, unow())
        setTimeout(()=>super.mmExit(sender, {}), 100)
      }, 100)
    }
  } // mmExited

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Received automatically (through inheritance of msysListener) when configuration and all dependencies were fulfilled.
  * Note that dependancy is fulfilled also when it exited instead of started.
  * @param {object} sender - message sender
  * @param {object} params - not used
  */
  mmStart(sender, params) {
    if(MOSY_APP==='mServer' && require('msysDB').ERROR) {
      setTimeout(()=>{
        console.logger_worker.info('system', `ENDED APPLICATION ${MOSY_APP_WITH_VERSION}`, unow())
        setTimeout(()=>{
          try{
            process.exit()
          }catch(e) {
            this.closeGUIApplication()
          }
        }, 100)
      }, 100)
      return super.mmExit(sender, { error: 'database error' })
    }

    if(monitorEventLoopDelay && this.config.event_loop_check) {
      const h = monitorEventLoopDelay({ resolution: 10 })
      h.enable()

      setInterval(() => {
        const max_lag = h.max / 1e6
        if(max_lag >= this.config.event_loop_error_ms) {
          this.error(`Event loop lag: ${max_lag} ms`)
        }else if(max_lag >= this.config.event_loop_warn_ms) {
          this.warn(`Event loop lag: ${max_lag} ms`)
        }else {
          this.debug(`Event loop lag: ${max_lag} ms`)
        }
        h.reset()
      }, 1000)
    }

    if(PerformanceObserver && this.config.gc_check) {
      const obs = new PerformanceObserver((list) => {
        for(const e of list.getEntries()) {
          const { entryType, duration, kind } = e
          if(entryType !== 'gc') continue
          if(duration >= this.config.gc_error_ms) {
            this.error(`GC pause (${kind}): ${duration.toFixed(1)} ms`)
          }else if(duration >= this.config.gc_warn_ms) {
            this.warn(`GC pause (${kind}): ${duration.toFixed(1)} ms`)
          }else {
            this.debug(`GC pause (${kind}): ${duration.toFixed(1)} ms`)
          }
        }
      })
      obs.observe({ entryTypes: ['gc'], buffered: true })
    }

    super.mmStart(sender, params)
  } // mmStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to exit the listener
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.delete] - if true, will be deleted from list of listeners
  * @param {string} [params.error] - reason for exit
  */
  mmExit(sender, params) {
    // don't exit directly in msysLogger; instead wait for all others to publish mmExited
  } // mmExit
} // msysLogger
